/**
 * Millie's hero slideshow.
 * Crossfades between slides, with autoplay that pauses on hover, on keyboard focus,
 * when the shopper presses pause, and never runs for people who prefer reduced motion.
 */
class MilliesHero extends HTMLElement {
  #index = 0;
  #timer = null;
  #pausedByUser = false;
  #hovering = false;
  #abort = new AbortController();

  connectedCallback() {
    this.slides = [...this.querySelectorAll('[data-slide]')];
    this.thumbs = [...this.querySelectorAll('[data-go]')];
    this.counter = this.querySelector('[data-current]');
    this.pauseButton = this.querySelector('[data-pause]');
    if (this.slides.length < 2) return;

    const { signal } = this.#abort;
    this.querySelector('[data-prev]')?.addEventListener('click', () => this.go(this.#index - 1), { signal });
    this.querySelector('[data-next]')?.addEventListener('click', () => this.go(this.#index + 1), { signal });
    this.thumbs.forEach((thumb) =>
      thumb.addEventListener('click', () => this.go(Number(thumb.dataset.go)), { signal })
    );
    this.pauseButton?.addEventListener('click', () => this.#togglePause(), { signal });

    this.addEventListener('mouseenter', () => ((this.#hovering = true), this.#stop()), { signal });
    this.addEventListener('mouseleave', () => ((this.#hovering = false), this.#start()), { signal });
    this.addEventListener('focusin', () => this.#stop(), { signal });
    this.addEventListener('focusout', (event) => {
      if (!this.contains(/** @type {Node} */ (event.relatedTarget))) this.#start();
    }, { signal });
    this.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') this.go(this.#index - 1);
      if (event.key === 'ArrowRight') this.go(this.#index + 1);
    }, { signal });

    // Theme editor: jump to the slide being edited.
    document.addEventListener('shopify:block:select', (event) => {
      const i = this.slides.indexOf(/** @type {HTMLElement} */ (event.target));
      if (i > -1) {
        this.#pausedByUser = true;
        this.go(i);
      }
    }, { signal });

    this.#start();
  }

  disconnectedCallback() {
    this.#abort.abort();
    this.#stop();
  }

  get #canAutoplay() {
    return (
      this.dataset.autoplay === 'true' &&
      !this.#pausedByUser &&
      !this.#hovering &&
      !window.matchMedia('(prefers-reduced-motion: reduce)').matches
    );
  }

  #start() {
    this.#stop();
    if (!this.#canAutoplay) return;
    const speed = Number(this.dataset.speed) || 5000;
    this.#timer = setInterval(() => this.go(this.#index + 1, false), speed);
  }

  #stop() {
    clearInterval(this.#timer);
    this.#timer = null;
  }

  #togglePause() {
    this.#pausedByUser = !this.#pausedByUser;
    const btn = this.pauseButton;
    btn.classList.toggle('is-paused', this.#pausedByUser);
    btn.setAttribute('aria-label', this.#pausedByUser ? btn.dataset.labelPlay : btn.dataset.labelPause);
    this.#pausedByUser ? this.#stop() : this.#start();
  }

  go(i, restart = true) {
    const count = this.slides.length;
    const next = ((i % count) + count) % count;
    if (next === this.#index) return;

    this.slides.forEach((slide, n) => {
      const active = n === next;
      slide.classList.toggle('is-active', active);
      slide.toggleAttribute('inert', !active);
      if (active) slide.removeAttribute('aria-hidden');
      else slide.setAttribute('aria-hidden', 'true');
    });
    this.thumbs.forEach((thumb, n) => {
      thumb.classList.toggle('is-active', n === next);
      if (n === next) thumb.setAttribute('aria-current', 'true');
      else thumb.removeAttribute('aria-current');
    });
    if (this.counter) this.counter.textContent = String(next + 1).padStart(2, '0');

    this.#index = next;
    if (restart) this.#start();
  }
}

if (!customElements.get('millies-hero')) customElements.define('millies-hero', MilliesHero);
